/* Impulse 10.0.0 collection adapter; native AjaxRenderer and Collection contracts verified. */
(() => {
  'use strict';
  if (window.LLCollectionEnhancement) return;
  const ROOT = '[data-section-type="collection-grid"]';
  const GRID = '.collection-grid__wrapper > .grid.grid--uniform';
  const PAGINATION = '#CollectionAjaxContent .pagination';
  const CARD = '.grid-product[data-product-id][data-product-handle]';
  const SECTION_CONTRACT = 'impulse-10.0.0-v1';
  const instances = new Map();
  const unloaded = new WeakSet();
  const lifecycle = new AbortController();
  const PREVIEW_PARAMS = new Set(['preview_theme_id', '_ab', '_fd', '_sc']);
  class CollectionLoadError extends Error {}
  const isPreviewParameter = key => PREVIEW_PARAMS.has(key) ||
    (key === 'oseid' && window.Shopify?.designMode);
  let pageHidden = false;
  const viewKey = () => location.pathname + location.search;
  function supportsContinuous(metadata = window.Shopify?.theme) {
    return metadata?.schema_name === 'Impulse' && metadata?.schema_version === '10.0.0' &&
      Number.isSafeInteger(metadata.id) && metadata.id > 0;
  }
  function isCollectionRenderer(renderer) {
    return renderer?.sections?.length === 1 &&
      renderer.sections[0]?.nodeId === 'CollectionAjaxContent';
  }
  function installCollectionRendererGuard() {
    const Renderer = window.theme?.AjaxRenderer;
    const prototype = Renderer?.prototype;
    if (!supportsContinuous() || !prototype || prototype.__llCollectionGuard1000 ||
        typeof prototype.renderPage !== 'function' ||
        typeof prototype.renderSectionFromFetch !== 'function') return;
    const nativeRenderPage = prototype.renderPage;
    const nativeRenderSectionFromFetch = prototype.renderSectionFromFetch;
    prototype.__llCollectionGuard1000 = true;
    prototype.renderPage = function () {
      if (!isCollectionRenderer(this) || !supportsContinuous()) {
        return nativeRenderPage.apply(this, arguments);
      }
      this.__llCollectionGeneration = (this.__llCollectionGeneration || 0) + 1;
      return nativeRenderPage.apply(this, arguments);
    };
    prototype.renderSectionFromFetch = function (url, section) {
      if (!isCollectionRenderer(this) || !supportsContinuous()) {
        return nativeRenderSectionFromFetch.apply(this, arguments);
      }
      const generation = this.__llCollectionGeneration;
      return Promise.resolve().then(() => fetch(url)).then(response => {
        if (!response.ok) throw new Error('Collection request failed');
        return response.text();
      }).then(html => {
        if (generation !== this.__llCollectionGeneration) return section;
        this.cachedSections = [...this.cachedSections, { html, url }];
        this.renderSection(html, section);
        return section;
      }).catch(() => {
        if (generation !== this.__llCollectionGeneration) return section;
        setTimeout(() => {
          if (generation !== this.__llCollectionGeneration || !supportsContinuous()) return;
          try { location.replace(location.href); } catch {}
        }, 0);
        // Resolve so Impulse releases its loading lock before the native fallback.
        return section;
      });
    };
  }
  function hasSectionIdentity(root, themeId = window.Shopify?.theme?.id) {
    // The Liquid marker identifies this audited section; Shopify.theme gates the running version.
    return supportsContinuous() && !!root?.dataset.sectionId &&
      root.dataset.llCollectionThemeId === String(themeId) &&
      root.dataset.llCollectionContract === SECTION_CONTRACT;
  }
  function queryKey(url, all = false) {
    const entries = [...url.searchParams].filter(([key]) => all
      ? !isPreviewParameter(key)
      : key.startsWith('filter.') || key === 'sort_by');
    entries.sort(([a, av], [b, bv]) => a < b ? -1 : a > b ? 1 : av < bv ? -1 : av > bv ? 1 : 0);
    return JSON.stringify(entries);
  }
  function localURL(href, base = location.href, sectionId) {
    const url = new URL(href, base);
    if (url.origin !== location.origin || url.pathname !== location.pathname) {
      throw new CollectionLoadError('Collection route changed');
    }
    const sections = url.searchParams.getAll('section_id');
    if ((sections.length && (sections.length !== 1 || sections[0] !== sectionId)) ||
        [...url.searchParams.keys()].some(key => key === 'sections' || key.startsWith('sections[') ||
          key === 'sections_url' || key.startsWith('section_id['))) {
      throw new CollectionLoadError('Section request changed');
    }
    // Only the verified single-section parameter may be removed from page links.
    url.searchParams.delete('section_id');
    const previews = url.searchParams.getAll('preview_theme_id');
    if (previews.length > 1 || (previews.length && previews[0] !== String(window.Shopify?.theme?.id))) {
      throw new CollectionLoadError('Preview theme changed');
    }
    const editorIds = url.searchParams.getAll('oseid');
    if (editorIds.length) {
      const currentIds = new URL(location.href).searchParams.getAll('oseid');
      // Shopify adds this preview identity to editor requests; accept only this editor's exact value.
      if (!window.Shopify?.designMode || editorIds.length !== 1 || currentIds.length !== 1 ||
          !currentIds[0] || editorIds[0] !== currentIds[0]) throw new CollectionLoadError('Editor preview changed');
    }
    // Native next links omit preview identity; keep requests on the draft.
    if (window.Shopify?.theme?.role === 'unpublished') {
      url.searchParams.set('preview_theme_id', String(window.Shopify.theme.id));
    }
    url.hash = '';
    return url;
  }
  function linkHeadings(scope) {
    const sections = [...scope.querySelectorAll(ROOT + '[data-context="featured-collection"]')];
    if (scope.matches?.(ROOT + '[data-context="featured-collection"]')) sections.unshift(scope);
    for (const section of sections) {
      const header = section.querySelector('.section-header');
      const title = header?.querySelector('.section-header__title');
      if (!title) continue;
      const existing = title.querySelector('a[href]');
      if (existing) {
        existing.classList.add('ll-collection-heading-link');
        continue;
      }
      const viewAll = header.querySelector('a.section-header__link[href]');
      if (!viewAll) continue;
      let destination;
      try { destination = new URL(viewAll.getAttribute('href'), location.href); } catch { continue; }
      if (destination.origin !== location.origin ||
          !/\/collections\/[^/]+\/?$/.test(destination.pathname)) continue;
      const anchor = document.createElement('a');
      anchor.href = destination.href;
      anchor.className = 'll-collection-heading-link';
      anchor.append(...title.childNodes);
      title.append(anchor);
    }
  }
  function compatibleCards(cards) {
    if (!supportsContinuous()) return false;
    const theme = window.theme;
    if (cards.some(card => card.querySelector('.quick-product__btn')) &&
        (!theme?.config || typeof theme?.settings?.quickView !== 'boolean' ||
         typeof theme?.preloadProductModal !== 'function')) return false;
    if (cards.some(card => card.querySelector('.color-swatch--with-image')) &&
        typeof theme?.Collection?.prototype?.colorSwatchHovering !== 'function') return false;
    return true;
  }
  // Native 10.0.0 keeps both Collection color swatch and quick shop entry points.
  function prepareCards(cards, signal) {
    for (const card of cards) {
      if (card.querySelector('.color-swatch--with-image')) {
        window.theme.Collection.prototype.colorSwatchHovering.call({ container: card });
      }
      if (window.theme?.settings?.quickView && card.querySelector('.quick-product__btn')) {
        const preload = () => {
          if (!supportsContinuous() || window.theme.config.bpSmall) return;
          card.removeEventListener('mouseover', preload);
          card.removeEventListener('focusin', preload);
          window.theme.preloadProductModal(
            card.dataset.productHandle,
            card.dataset.productId,
            card.querySelector('.quick-product__btn')
          );
        };
        card.addEventListener('mouseover', preload, { signal });
        card.addEventListener('focusin', preload, { signal });
      }
    }
    window.AOS?.refreshHard?.();
  }
  // Restore a bounded browsing checkpoint without owning the theme's history state.
  const RETURN_STORE = 'll:collection:return:v1';
  const RETURN_PAGES = 3;
  const RETURN_MS = 6000;
  const RETURN_TTL = 30 * 60 * 1000;
  let returnPageShown = document.readyState === 'complete';
  let returnAttempted = false;

  function createReturnCheckpoint(controller) {
    let entry;
    try { entry = window.navigation?.currentEntry?.key; } catch {}
    const eligible = !!entry && !!window.crypto?.subtle && !window.Shopify?.designMode;
    const traversal = eligible && performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
    let pending = eligible && traversal && !returnAttempted;
    let active = false;
    let cancelled = false;
    let captured = null;
    let resumeAfterReturn = false;
    let scope = null;
    const scopeReady = eligible ? crypto.subtle.digest('SHA-256', new TextEncoder().encode(controller.view))
      .then(bytes => {
        const hash = [...new Uint8Array(bytes)].map(value => value.toString(16).padStart(2, '0')).join('');
        scope = [controller.themeId, 'Impulse', '10.0.0', SECTION_CONTRACT, controller.sectionId, hash].join(':');
      }).catch(() => {}) : Promise.resolve();

    function records() {
      const raw = sessionStorage.getItem(RETURN_STORE);
      if (!raw) return [];
      if (raw.length > 8192) throw new Error('Oversize checkpoint');
      const data = JSON.parse(raw);
      if (data?.v !== 1 || !Array.isArray(data.entries) || data.entries.length > 8) return [];
      const keys = data.entries.map(item => item?.entry);
      if (new Set(keys).size !== keys.length) return [];
      const now = Date.now();
      return data.entries.filter(item => item &&
        typeof item.entry === 'string' && item.entry.length <= 128 &&
        typeof item.scope === 'string' && item.scope.length <= 512 &&
        Number.isFinite(item.at) && now >= item.at && now - item.at < RETURN_TTL &&
        Number.isInteger(item.depth) && item.depth >= 1 && item.depth <= RETURN_PAGES &&
        typeof item.product === 'string' && /^\d{1,20}$/.test(item.product) && Number.isFinite(item.top) && Math.abs(item.top) <= 4096)
        .map(({ entry, scope, at, depth, product, top }) => ({ entry, scope, at, depth, product, top }));
    }
    function store(entries) {
      const raw = JSON.stringify({ v: 1, entries: entries.slice(-8) });
      if (raw.length > 8192) throw new Error('Oversize checkpoint');
      sessionStorage.setItem(RETURN_STORE, raw);
    }
    function cancel() {
      captured = null;
      resumeAfterReturn = false;
      cancelled = true;
      if (active || pending) {
        returnAttempted = true;
        controller.paused = true;
        controller.request?.abort();
      }
    }
    function save() {
      if (!eligible || !scope || !controller.current() || controller.view !== viewKey()) return;
      try {
        const others = records().filter(item => item.entry !== entry);
        if (controller.seen.size < 1 || controller.seen.size > RETURN_PAGES) {
          store(others);
          return;
        }
        let anchor = captured && Date.now() - captured.at <= 2000 ? captured : null;
        if (!anchor) {
          const card = [...controller.grid.children].find(node => node.matches(CARD) &&
            node.getBoundingClientRect().bottom > 0 && node.getBoundingClientRect().top < innerHeight);
          if (card) anchor = { product: card.dataset.productId, top: card.getBoundingClientRect().top };
        }
        if (!anchor) { store(others); return; }
        store([...others, { entry, scope, product: anchor.product, top: anchor.top,
          depth: controller.seen.size, at: Date.now() }]);
      } catch {}
    }
    async function restore() {
      if (!pending || !returnPageShown || returnAttempted) return;
      returnAttempted = true;
      let attempted = false;
      let deadline;
      try {
        await scopeReady;
        if (!scope || cancelled || !controller.current() || controller.view !== viewKey()) return;
        const entries = records();
        const record = entries.find(item => item.entry === entry && item.scope === scope);
        if (!record) return;
        // Consume before requesting; a denied write must also disable replay.
        store(entries.filter(item => item.entry !== entry));
        attempted = true;
        active = true;
        const until = performance.now() + RETURN_MS;
        deadline = setTimeout(cancel, RETURN_MS);
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        for (let count = 0; count < record.depth && count < RETURN_PAGES; count++) {
          if (cancelled || performance.now() >= until || !controller.current() ||
              controller.view !== viewKey() || pageHidden) return;
          const before = controller.seen.size;
          await controller.load();
          if (cancelled || controller.seen.size !== before + 1 || controller.paused) return;
        }
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        if (cancelled || performance.now() >= until || !controller.current() ||
            controller.view !== viewKey() || pageHidden) return;
        const card = [...controller.grid.children].find(node => node.matches(CARD) &&
          node.dataset.productId === record.product);
        if (!card) return;
        const top = Math.max(-innerHeight, Math.min(record.top, innerHeight - 48));
        window.scrollBy({ top: card.getBoundingClientRect().top - top, left: 0, behavior: 'instant' });
        resumeAfterReturn = true;
      } catch {
        // Checkpoint storage is optional; existing pagination remains the fallback.
      } finally {
        clearTimeout(deadline);
        active = false;
        pending = false;
        // Do not let IntersectionObserver turn bounded replay into another request.
        if (attempted) controller.paused = true;
        if (controller.current()) controller.arm();
      }
    }
    document.addEventListener('click', event => {
      if (!eligible || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.('a[href]');
      const card = anchor?.closest(CARD);
      if (!card || !controller.grid.contains(card) || anchor.hasAttribute('download') ||
          (anchor.target && anchor.target !== '_self')) return;
      let url;
      try { url = new URL(anchor.href, location.href); } catch { return; }
      if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname) ||
          !/^\d{1,20}$/.test(card.dataset.productId) || controller.seen.size < 1 ||
          controller.seen.size > RETURN_PAGES) return;
      const snapshot = { at: Date.now(), depth: controller.seen.size,
        product: card.dataset.productId, top: card.getBoundingClientRect().top };
      queueMicrotask(() => { if (!event.defaultPrevented) captured = snapshot; });
    }, { signal: controller.events.signal });
    for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown', 'click', 'beforeinput', 'input', 'compositionstart']) {
      window.addEventListener(type, event => {
        if (event.isTrusted && (active || pending)) cancel();
      }, { capture: true, passive: true, signal: controller.events.signal });
    }
    for (const type of ['wheel', 'touchmove', 'keydown']) {
      window.addEventListener(type, event => {
        if (!event.isTrusted || !resumeAfterReturn || active || pending ||
            !controller.current() || controller.view !== viewKey() || pageHidden) return;
        if (type === 'keydown' && (!['ArrowDown', 'PageDown', 'End', ' '].includes(event.key) ||
            event.target.closest?.('input, textarea, select, [contenteditable="true"]'))) return;
        resumeAfterReturn = false;
        controller.paused = false;
        controller.arm();
      }, { capture: true, passive: true, signal: controller.events.signal });
    }
    window.addEventListener('popstate', () => {
      if ((active || pending) && (window.navigation?.currentEntry?.key !== entry ||
          controller.view !== viewKey())) cancel();
    },
      { signal: controller.events.signal });
    queueMicrotask(restore);
    return { save, restore, cancel, get holding() { return pending || active; },
      get canResume() { return resumeAfterReturn; } };
  }

  class ContinuousCollection {
    constructor(root, grid, pagination) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.themeId = window.Shopify.theme.id;
      this.grid = grid;
      this.pagination = pagination;
      this.view = viewKey();
      this.seen = new Set();
      this.events = new AbortController();
      this.busy = false;
      this.paused = false;
      this.dead = false;
      this.controls = document.createElement('div');
      this.controls.className = 'll-collection-load';
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'btn btn--secondary';
      this.button.textContent = 'Load more';
      this.status = document.createElement('span');
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.controls.append(this.button, this.status);
      pagination.before(this.controls);
      this.button.addEventListener('click', event => {
        this.paused = false;
        this.load({
          focusAppended: event.detail === 0 && document.activeElement === this.button
        });
      }, { signal: this.events.signal });
      this.intersection = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && !this.paused) this.load();
      }, { rootMargin: '0px 0px 500px 0px' });
      this.changes = new MutationObserver(() => {
        if (!this.current()) {
          this.destroy();
          queueMicrotask(() => mount(root));
        } else if (grid.parentElement.classList.contains('unload')) {
          this.suspend();
        }
      });
      this.changes.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      this.returnCheckpoint = createReturnCheckpoint(this);
      this.arm();
    }
    current() {
      return !this.dead && hasSectionIdentity(this.root, this.themeId) &&
        window.Shopify.theme.id === this.themeId && this.root.dataset.sectionId === this.sectionId &&
        this.root.isConnected && this.root.querySelector(GRID) === this.grid;
    }
    next() {
      const anchor = this.pagination.querySelector('.next a[href]');
      if (!anchor) return null;
      const url = localURL(anchor.getAttribute('href'), location.href, this.sectionId);
      if (queryKey(url) !== queryKey(new URL(location.href))) throw new CollectionLoadError('Next page filters changed');
      return url;
    }
    arm() {
      this.intersection.disconnect();
      if (this.returnCheckpoint?.holding) return;
      if (!supportsContinuous()) { this.destroy(); return; }
      let next;
      try { next = this.next(); } catch { this.suspend(); return; }
      this.button.hidden = !next;
      if (next && !this.seen.has(next.href) && !this.paused && !pageHidden && !window.Shopify?.designMode) {
        this.intersection.observe(this.controls);
      }
    }
    suspend() {
      this.returnCheckpoint?.cancel();
      this.paused = true;
      this.intersection.disconnect();
      this.request?.abort();
    }
    async load({ focusAppended = false } = {}) {
      if (!supportsContinuous()) { this.destroy(); return; }
      if (pageHidden || this.busy || !this.current() || this.view !== viewKey() ||
          this.grid.parentElement.classList.contains('unload')) return;
      let next;
      try { next = this.next(); } catch { this.suspend(); return; }
      if (!next || this.seen.has(next.href)) { this.suspend(); return; }
      this.busy = true;
      this.button.disabled = true;
      this.status.textContent = 'Loading products...';
      this.grid.setAttribute('aria-busy', 'true');
      this.intersection.disconnect();
      const request = this.request = new AbortController();
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; request.abort(); }, 20000);
      request.signal.addEventListener('abort', () => clearTimeout(timeout), { once: true });
      let appended = [];
      try {
        const sectionURL = new URL(next.href);
        sectionURL.searchParams.set('section_id', this.sectionId);
        const response = await fetch(sectionURL.href, { signal: request.signal, credentials: 'same-origin' });
        if (!response.ok || !response.url) throw new CollectionLoadError('Page request failed');
        if (new URL(response.url).searchParams.getAll('section_id').length !== 1) {
          throw new CollectionLoadError('Missing section response URL');
        }
        const responseURL = localURL(response.url, location.href, this.sectionId);
        if (queryKey(responseURL, true) !== queryKey(next, true)) {
          const keys = [...new Set([...next.searchParams.keys(), ...responseURL.searchParams.keys()])];
          const changed = keys.filter(key => !isPreviewParameter(key) &&
            JSON.stringify(next.searchParams.getAll(key).sort()) !==
            JSON.stringify(responseURL.searchParams.getAll(key).sort()));
          throw new CollectionLoadError('Response query changed: ' + changed.join(', '));
        }
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const remoteRoots = [...doc.querySelectorAll(ROOT)].filter(node => node.dataset.sectionId === this.sectionId);
        if (remoteRoots.length !== 1 || !hasSectionIdentity(remoteRoots[0], this.themeId)) {
          throw new CollectionLoadError('Response section identity changed');
        }
        const remoteRoot = remoteRoots[0];
        const remoteGrids = remoteRoot.querySelectorAll(GRID);
        const remotePaginations = remoteRoot?.querySelectorAll(PAGINATION);
        if (remoteGrids.length !== 1 || remotePaginations?.length !== 1) throw new CollectionLoadError('Collection contract changed');
        const remoteGrid = remoteGrids[0];
        const remotePagination = remotePaginations[0];
        const ids = new Set([...this.grid.children].filter(node => node.matches(CARD)).map(node => node.dataset.productId));
        for (const card of remoteGrid.children) {
          if (!card.matches(CARD) || ids.has(card.dataset.productId)) continue;
          ids.add(card.dataset.productId);
          const copy = document.importNode(card, true);
          // Preserve JSON product data, but never execute downloaded page scripts.
          copy.querySelectorAll('script').forEach(script => {
            if (!['application/json', 'application/ld+json'].includes(script.type)) script.remove();
          });
          appended.push(copy);
        }
        if (!appended.length || !compatibleCards(appended)) throw new CollectionLoadError('No compatible new cards');
        if (!this.current() || this.view !== viewKey() || request.signal.aborted) return;
        const replacement = document.importNode(remotePagination, true);
        for (const anchor of replacement.querySelectorAll('a[href]')) {
          const url = localURL(anchor.getAttribute('href'), responseURL.href, this.sectionId);
          if (queryKey(url) !== queryKey(next)) throw new CollectionLoadError('Pagination filters changed');
          anchor.href = url.href;
        }
        this.grid.append(...appended);
        try {
          prepareCards(appended, this.events.signal);
        } catch (error) {
          appended.forEach(card => card.remove());
          throw error;
        }
        this.pagination.replaceWith(replacement);
        this.pagination = replacement;
        this.seen.add(next.href);
        this.status.textContent = appended.length + ' more products loaded.';
        if (focusAppended && [this.button, document.body, document.documentElement].includes(document.activeElement)) {
          appended[0].querySelector('a[href], button, input, select, textarea, [tabindex]:not([tabindex^="-"])')?.focus();
        }
        // Swatch King owns its observed continuous lookup; do not recreate its UI or data.
        document.dispatchEvent(new CustomEvent('ll:collection:appended', {
          detail: { section: this.root, cards: appended, url: next.href }
        }));
      } catch (error) {
        if (!request.signal.aborted || timedOut) {
          if (window.Shopify?.designMode) {
            const diagnostic = error instanceof CollectionLoadError
              ? 'Error: ' + error.message : 'Request or product initialization failed';
            console.warn('[LL collection] ' + diagnostic);
          }
          this.paused = true;
          if (this.current() && this.view === viewKey()) {
            this.status.textContent = 'Products could not be loaded. Try again or use the page links.';
          }
        }
      } finally {
        clearTimeout(timeout);
        if (request.signal.aborted && !timedOut) this.status.textContent = '';
        if (this.request === request) this.request = null;
        this.busy = false;
        this.button.disabled = false;
        this.grid.removeAttribute('aria-busy');
        if (!supportsContinuous()) this.destroy();
        else if (this.current()) this.arm();
      }
    }
    destroy() {
      if (this.dead) return;
      this.dead = true;
      this.returnCheckpoint?.cancel();
      this.request?.abort();
      this.events.abort();
      this.intersection.disconnect();
      this.changes.disconnect();
      this.controls.remove();
      this.grid.removeAttribute('aria-busy');
      instances.delete(this.root);
    }
  }
  function mount(root) {
    if (!hasSectionIdentity(root) || pageHidden || !root.isConnected || unloaded.has(root) || instances.has(root) ||
        root.dataset.context === 'featured-collection' ||
        !root.querySelector('#CollectionAjaxContent') || !window.IntersectionObserver) return;
    const grids = root.querySelectorAll(GRID);
    const paginations = root.querySelectorAll(PAGINATION);
    if (grids.length !== 1 || paginations.length !== 1 ||
        grids[0].parentElement.classList.contains('unload')) return;
    const pagination = paginations[0];
    if (!pagination.querySelector('.next a[href]')) return;
    if (!compatibleCards([...grids[0].children].filter(node => node.matches(CARD)))) return;
    instances.set(root, new ContinuousCollection(root, grids[0], pagination));
  }
  function refresh(scope = document) {
    linkHeadings(scope);
    for (const [root, controller] of instances) {
      if (!controller.current() || controller.view !== viewKey()) controller.destroy();
    }
    if (scope.matches?.(ROOT)) mount(scope);
    scope.querySelectorAll(ROOT).forEach(mount);
  }
  const suspendAll = () => instances.forEach(controller => controller.suspend());
  let keyboardFilterAt = 0;
  let filterFocusToken = null;
  document.addEventListener('keydown', event => {
    if (['Enter', ' ', 'Spacebar'].includes(event.key)) keyboardFilterAt = Date.now();
  }, { capture: true, signal: lifecycle.signal });
  document.addEventListener('pointerdown', event => {
    if (!event.isTrusted) return;
    keyboardFilterAt = 0;
    filterFocusToken = null;
  }, { capture: true, signal: lifecycle.signal });
  function rememberFilterFocus(event) {
    if (event.type !== 'input' || Date.now() - keyboardFilterAt > 1500 ||
        !window.theme?.config?.bpSmall ||
        !event.target.matches?.('.filter-form input')) return;
    const token = filterFocusToken = {
      at: Date.now(),
      path: location.pathname,
      opener: document.querySelector('.js-drawer-open-collection-filters'),
      source: event.target.closest('#FilterDrawer')
    };
    const handoff = () => {
      if (filterFocusToken !== token || !window.LLCollectionEnhancement ||
          token.path !== location.pathname || !window.theme?.config?.bpSmall) return;
      const active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement &&
          active !== token.opener && !(token.source?.isConnected && token.source.contains(active))) {
        filterFocusToken = null;
        return;
      }
      const opener = document.querySelector('.js-drawer-open-collection-filters');
      const drawerOpen = document.getElementById('FilterDrawer')?.classList.contains('drawer--is-open');
      if (Date.now() - token.at >= 550 && opener && opener !== token.opener && !drawerOpen) {
        filterFocusToken = null;
        opener.focus({ preventScroll: true });
        return;
      }
      if (Date.now() - token.at < 30000) setTimeout(handoff, 100);
      else filterFocusToken = null;
    };
    setTimeout(handoff, 100);
  }
  const onNavigation = event => {
    const selector = event.type === 'click'
      ? '.tag-list--active-tags a, .pagination a'
      : '.filter-form, #SortBy';
    if (event.target.closest?.(selector)) suspendAll();
    rememberFilterFocus(event);
  };
  for (const name of ['input', 'change', 'click']) {
    document.addEventListener(name, onNavigation, { capture: true, signal: lifecycle.signal });
  }
  window.addEventListener('popstate', () => {
    filterFocusToken = null;
    instances.forEach(controller => {
      if (controller.view !== viewKey()) controller.suspend();
    });
  }, { signal: lifecycle.signal });
  window.addEventListener('pagehide', () => {
    pageHidden = true;
    instances.forEach(controller => {
      controller.returnCheckpoint?.save();
      controller.resumeOnShow = !controller.paused || controller.returnCheckpoint?.canResume;
      controller.suspend();
    });
  }, { signal: lifecycle.signal });
  window.addEventListener('pageshow', event => {
    pageHidden = false;
    returnPageShown = true;
    if (!event.persisted) {
      instances.forEach(controller => controller.returnCheckpoint?.restore());
      return;
    }
    linkHeadings(document);
    instances.forEach(controller => {
      if (!controller.current()) { controller.destroy(); return; }
      if (controller.resumeOnShow && controller.view === viewKey() &&
          !controller.grid.parentElement.classList.contains('unload')) {
        controller.paused = false;
        controller.arm();
      }
      controller.resumeOnShow = false;
    });
    document.querySelectorAll(ROOT).forEach(mount);
  }, { signal: lifecycle.signal });
  for (const name of ['DOMContentLoaded', 'page:loaded']) {
    document.addEventListener(name, () => refresh(), { signal: lifecycle.signal });
  }
  document.addEventListener('collection:reloaded', () => {
    instances.forEach(controller => controller.destroy());
    refresh();
  }, { signal: lifecycle.signal });
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, controller] of instances) {
      if (event.target === root || event.target.contains(root)) {
        unloaded.add(root);
        controller.destroy();
      }
    }
  }, { signal: lifecycle.signal });
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll(ROOT).forEach(root => unloaded.delete(root));
    unloaded.delete(event.target);
    refresh(event.target);
  }, { signal: lifecycle.signal });
  window.LLCollectionEnhancement = {
    refresh,
    destroy() {
      filterFocusToken = null;
      lifecycle.abort();
      instances.forEach(controller => controller.destroy());
      delete window.LLCollectionEnhancement;
    }
  };
  installCollectionRendererGuard();
  if (document.readyState !== 'loading') refresh();
})();
